import { useRef, useCallback, useEffect } from "react";
import { saveEditorEvents, serverURL } from "../services/assessment.api";
import { serverURL as fallbackServerURL } from "../utils/programming/apiCall";

// Event throttling constants
const CHANGE_THROTTLE_MS = 16; // ~60fps for content changes
const CURSOR_THROTTLE_MS = 50; // 20fps for cursor movements
const SELECTION_THROTTLE_MS = 100; // 10fps for selections
const FOCUS_THROTTLE_MS = 200; // 5fps for focus events

// Buffer management constants
const MAX_EVENT_BUFFER_SIZE = 5000; // Increased buffer size
const BUFFER_CLEANUP_THRESHOLD = 4000; // Cleanup threshold

/**
 * Hook for tracking Monaco Editor events
 * @param {object} options - Configuration options
 * @param {string} options.questionId - Question ID
 * @param {number} options.selectedLanguage - Selected language ID
 * @param {string} options.candidateAssessmentId - Candidate assessment ID
 * @param {string} options.assessmentId - Screening test ID
 * @param {string} options.skillName - Skill name
 * @param {Array} options.supportedLanguages - Supported languages array
 * @param {boolean} options.practiceMode - Whether in practice mode
 * @param {string} options.questionTitle - Question title (optional)
 * @param {string} options.questionType - Question type (optional, defaults to "programming")
 * @returns {object} Event tracking functions and buffer ref
 */
export const useEditorEventTracking = ({
  questionId,
  selectedLanguage,
  candidateAssessmentId,
  assessmentId,
  skillName,
  supportedLanguages,
  practiceMode,
  questionTitle = null,
  questionType = "programming",
}) => {
  // Event buffer and tracking refs
  const eventBufferRef = useRef([]);
  const flushTimerRef = useRef(null);
  const sessionStartTimeRef = useRef(null);
  const eventSequenceRef = useRef(0); // Ensure proper event ordering

  // Separate throttling for different event types
  const lastChangeEventTimeRef = useRef(0);
  const lastCursorEventTimeRef = useRef(0);
  const lastSelectionEventTimeRef = useRef(0);
  const lastFocusEventTimeRef = useRef(0);

  /**
   * Comprehensive event tracking function
   */
  const trackEditorEvent = useCallback(
    (eventType, eventData, throttleMs = 0, lastEventTimeRef) => {
      const now = performance.now(); // Use high-resolution timestamp

      // Check throttling
      if (
        throttleMs > 0 &&
        lastEventTimeRef &&
        now - lastEventTimeRef.current < throttleMs
      ) {
        return;
      }

      if (lastEventTimeRef) {
        lastEventTimeRef.current = now;
      }

      // Calculate relative time from session start
      const relativeTime = sessionStartTimeRef.current
        ? now - sessionStartTimeRef.current
        : 0;

      // Create event with proper sequencing
      const event = {
        t: Math.round(relativeTime), // Round to nearest millisecond
        seq: ++eventSequenceRef.current, // Ensure proper ordering
        type: eventType,
        timestamp: now, // High-resolution timestamp for debugging
        ...eventData,
      };

      // Add to buffer immediately (no async delays)
      eventBufferRef.current.push(event);

      // Better buffer management
      if (eventBufferRef.current.length > MAX_EVENT_BUFFER_SIZE) {
        // Keep events from the last 80% of the buffer
        const keepCount = Math.floor(MAX_EVENT_BUFFER_SIZE * 0.8);
        eventBufferRef.current = eventBufferRef.current.slice(-keepCount);
      }
    },
    [],
  );

  /**
   * Track initial state for proper session replay
   */
  const trackInitialState = useCallback(
    (initialCode, languageId) => {
      // Track initial state as a special event
      const initialStateEvent = {
        t: 0, // Always at time 0
        seq: 0, // First event in sequence
        type: "initialState",
        timestamp: sessionStartTimeRef.current,
        initialCode: initialCode || "",
        languageId: languageId,
        starterCode:
          supportedLanguages.find((lang) => lang.languageId === languageId)
            ?.starterCode || "",
      };

      // Insert at beginning of buffer
      eventBufferRef.current.unshift(initialStateEvent);
    },
    [supportedLanguages],
  );

  /**
   * Reset event tracking for new question/language
   */
  const resetEventTracking = useCallback(() => {
    eventBufferRef.current = [];
    sessionStartTimeRef.current = performance.now(); // Use high-resolution timing
    eventSequenceRef.current = 0; // Reset sequence counter
  }, []);

  /**
   * Flush events to server
   */
  const flushEvents = useCallback(async () => {
    const buf = eventBufferRef.current;
    if (!buf.length) return;

    // Sort events by sequence to ensure proper order
    const sortedEvents = [...buf].sort((a, b) => a.seq - b.seq);
    eventBufferRef.current = [];

    try {
      const payload = {
        candidateAssessmentId: candidateAssessmentId,
        assessmentId: assessmentId,
        questionId: questionId,
        languageId: selectedLanguage,
        skill: skillName || "Programming",
        events: sortedEvents,
        sessionStartTime: sessionStartTimeRef.current,
        totalEvents: sortedEvents.length,
        initialContext: {
          questionId: questionId,
          languageId: selectedLanguage,
          starterCode:
            supportedLanguages.find(
              (lang) => lang.languageId === selectedLanguage,
            )?.starterCode || "",
        },
      };

      await saveEditorEvents(assessmentId, payload);

      // ✅ NEW: If events were successfully saved, remove from IndexedDB if it exists
      const { indexedDBUploadManager } =
        await import("../utils/programming/indexedDBUploadManager");
      const { offlineModeManager } =
        await import("../utils/programming/offlineModeManager");
      if (offlineModeManager.isOfflineModeSupported()) {
        try {
          await indexedDBUploadManager.markEditorEventsCompleted(
            questionId,
            selectedLanguage,
          );
        } catch (cleanupError) {
          // Ignore cleanup errors
          console.warn(
            "Error cleaning up editor events from IndexedDB:",
            cleanupError,
          );
        }
      }
    } catch (e) {
      console.error("Failed to save enhanced editor events:", e);

      // ✅ NEW: Store events in IndexedDB if offline
      const { isOfflineError } =
        await import("../utils/programming/offlineHandler");
      const { indexedDBUploadManager } =
        await import("../utils/programming/indexedDBUploadManager");
      const { offlineModeManager } =
        await import("../utils/programming/offlineModeManager");

      const isOffline = isOfflineError(e);
      if (isOffline && offlineModeManager.isOfflineModeSupported()) {
        try {
          await indexedDBUploadManager.storePendingEditorEvents(questionId, {
            languageId: selectedLanguage,
            events: sortedEvents,
            candidateAssessmentId: candidateAssessmentId,
            assessmentId: assessmentId,
            skill: skillName,
            sessionStartTime: sessionStartTimeRef.current,
            questionTitle: questionTitle,
            questionType: questionType,
            initialContext: {
              questionId: questionId,
              languageId: selectedLanguage,
              starterCode:
                supportedLanguages.find(
                  (lang) => lang.languageId === selectedLanguage,
                )?.starterCode || "",
            },
          });

          // Clear buffer since events are stored in IndexedDB
          eventBufferRef.current = [];
        } catch (storageError) {
          console.error(
            "Error storing editor events in IndexedDB:",
            storageError,
          );
          // Re-queue events on failure with better error handling
          const eventsToRequeue = sortedEvents.slice(0, 200); // Increased re-queue limit
          eventBufferRef.current.unshift(...eventsToRequeue);
        }
      } else {
        // Re-queue events on failure with better error handling
        const eventsToRequeue = sortedEvents.slice(0, 200); // Increased re-queue limit
        eventBufferRef.current.unshift(...eventsToRequeue);
      }
    }
  }, [
    questionId,
    selectedLanguage,
    candidateAssessmentId,
    assessmentId,
    skillName,
    supportedLanguages,
    questionTitle,
    questionType,
  ]);

  /**
   * Flush remaining events on unload
   */
  const flushEventsOnUnload = useCallback(() => {
    // Skip flushing events on unload in practice mode
    if (practiceMode) {
      return;
    }

    const buf = eventBufferRef.current;
    if (!buf.length) return;

    // Sort events by sequence before sending
    const sortedEvents = [...buf].sort((a, b) => a.seq - b.seq);

    try {
      navigator.sendBeacon?.(
        `${serverURL}/public/assessment/${assessmentId}/editor-events`,
        new Blob(
          [
            JSON.stringify({
              candidateAssessmentId: candidateAssessmentId,
              assessmentId: assessmentId,
              questionId: questionId,
              languageId: selectedLanguage,
              skill: skillName,
              events: sortedEvents,
              sessionStartTime: sessionStartTimeRef.current,
              totalEvents: sortedEvents.length,
              initialContext: {
                questionId: questionId,
                languageId: selectedLanguage,
                starterCode:
                  supportedLanguages.find(
                    (lang) => lang.languageId === selectedLanguage,
                  )?.starterCode || "",
              },
              isUnloadFlush: true,
            }),
          ],
          { type: "application/json" },
        ),
      );
    } catch (error) {
      console.error("Failed to send unload events:", error);
    }
  }, [
    questionId,
    selectedLanguage,
    candidateAssessmentId,
    assessmentId,
    skillName,
    practiceMode,
  ]);

  // Periodic flush for editor events buffer (every 2s)
  useEffect(() => {
    if (!questionId || !selectedLanguage) return;

    // Skip editor events saving in practice mode
    if (practiceMode) {
      return;
    }

    if (flushTimerRef.current) clearInterval(flushTimerRef.current);
    flushTimerRef.current = setInterval(async () => {
      await flushEvents();
    }, 2000); // Reduced flush interval for better responsiveness

    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
    };
  }, [questionId, selectedLanguage, practiceMode, flushEvents]);

  // Reset tracking when question or language changes
  useEffect(() => {
    resetEventTracking();
  }, [questionId, selectedLanguage, resetEventTracking]);

  // Flush remaining events on unload
  useEffect(() => {
    const handler = () => {
      flushEventsOnUnload();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [flushEventsOnUnload]);

  // Track window/tab events
  useEffect(() => {
    if (practiceMode) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        trackEditorEvent("tab_switch", { hidden: true });
      } else {
        trackEditorEvent("tab_switch", { hidden: false });
      }
    };

    const handleWindowBlur = () => {
      trackEditorEvent("window_blur", { focused: false });
    };

    const handleWindowFocus = () => {
      trackEditorEvent("window_focus", { focused: true });
    };

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        trackEditorEvent("full_screen_exit", { fullscreen: false });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [practiceMode, trackEditorEvent]);

  return {
    eventBufferRef,
    trackEditorEvent,
    trackInitialState,
    resetEventTracking,
    flushEvents,
    flushEventsOnUnload,
    // Throttle refs for external use
    lastChangeEventTimeRef,
    lastCursorEventTimeRef,
    lastSelectionEventTimeRef,
    lastFocusEventTimeRef,
    // Throttle constants
    CHANGE_THROTTLE_MS,
    CURSOR_THROTTLE_MS,
    SELECTION_THROTTLE_MS,
    FOCUS_THROTTLE_MS,
  };
};
